import React, { useState } from 'react';

const RecommendedRequirements = () => {
    const [activeId, setActiveId] = useState(null);

    const requirements = [
        {
            id: 'os',
            icon: '💻',
            title: 'Operating System',
            spec: 'Windows 11 or macOS 13 (Ventura)+',
            reason: 'Modern operating systems provide superior memory allocation to web browsers, preventing background tasks from choking the active exam session.'
        },
        {
            id: 'cpu',
            icon: '⚡',
            title: 'Processor (CPU)',
            spec: 'Intel Core i5 / i7 (8th Gen+) or AMD Ryzen 5+',
            reason: 'Rendering a 2000-line spreadsheet and executing complex data serialization for AI valuation demands strong single-core and multi-core JavaScript processing power.'
        },
        {
            id: 'ram',
            icon: '🧠',
            title: 'Memory (RAM)',
            spec: '16 GB RAM',
            reason: 'Absolutely critical. A 2000-line interactive grid consumes significant browser memory. 16 GB ensures the tab will not crash or discard while the AI processes massive data sets.'
        },
        {
            id: 'display',
            icon: '🖥️',
            title: 'Screen Resolution',
            spec: '1920 x 1080 (Full HD) or higher',
            reason: 'Navigating extensive spreadsheets requires maximum screen real estate to view data structures alongside exhibits and the AI feedback pane without excessive scrolling.'
        },
        {
            id: 'network',
            icon: '🚀',
            title: 'Internet Connection',
            spec: '10 Mbps or higher (Wired Ethernet)',
            reason: 'Transmitting thousands of rows of cell data for rapid AI valuation requires high bandwidth and low latency to prevent timeout errors and ensure instant auto-saving.'
        }
    ];

    const toggleAccordion = (id) => {
        setActiveId(activeId === id ? null : id);
    };

    return (
        <div className="max-w-4xl mx-auto my-12 p-[2px] rounded-2xl bg-gradient-to-br from-indigo-900 via-blue-600 to-teal-400 shadow-2xl">
            {/* Inner Container to create the gradient border effect */}
            <div className="bg-white rounded-2xl p-8 md:p-10 h-full w-full">
                
                {/* Header */}
                <div className="text-center mb-12">
                    <span className="inline-block py-1 px-3 rounded-full bg-teal-50 text-teal-700 text-sm font-bold tracking-wider mb-4 border border-teal-200">
                        SYSTEM CHECK
                    </span>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-900 to-blue-600 mb-4 tracking-tight">
                        Recommended System Specifications
                    </h2>
                    <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
                        Our platform features heavy-duty 2000-line spreadsheets and advanced AI valuation. To experience a seamless, crash-free exam environment, your device should meet these specifications. Click each item to learn why.
                    </p>
                </div>

                {/* Interactive Accordion List */}
                <div className="space-y-4 mb-12">
                    {requirements.map((req) => (
                        <div 
                            key={req.id} 
                            className={`border transition-all duration-300 rounded-xl overflow-hidden ${
                                activeId === req.id 
                                ? 'border-blue-500 shadow-lg bg-blue-50/40 transform scale-[1.01]' 
                                : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                            }`}
                        >
                            <button
                                onClick={() => toggleAccordion(req.id)}
                                className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
                            >
                                <div className="flex items-center gap-5">
                                    <span className="text-3xl filter drop-shadow-sm">{req.icon}</span>
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-lg">{req.title}</h3>
                                        <p className="text-md font-semibold text-blue-600 mt-1">{req.spec}</p>
                                    </div>
                                </div>
                                <div className={`text-blue-500 transform transition-transform duration-300 ${activeId === req.id ? 'rotate-180' : 'rotate-0'}`}>
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </button>
                            
                            {/* Expandable Content */}
                            <div 
                                className={`px-6 overflow-hidden transition-all duration-400 ease-in-out ${
                                    activeId === req.id ? 'max-h-48 pb-6 opacity-100' : 'max-h-0 opacity-0'
                                }`}
                            >
                                <p className="text-gray-700 text-md border-t border-blue-100 pt-4 mt-2 leading-relaxed">
                                    {req.reason}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Browser Requirement & Download Link */}
                <div className="relative overflow-hidden bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200 flex flex-col md:flex-row items-center justify-between gap-8 shadow-sm">
                    {/* Decorative background element */}
                    <div className="absolute -right-10 -top-10 w-40 h-40 bg-blue-100 rounded-full blur-3xl opacity-50 pointer-events-none"></div>
                    
                    <div className="flex items-center gap-6 relative z-10">
                        <img 
                            src="https://upload.wikimedia.org/wikipedia/commons/e/e1/Google_Chrome_icon_%28February_2022%29.svg" 
                            alt="Google Chrome" 
                            className="w-16 h-16 drop-shadow-md"
                        />
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-1">Optimized for Google Chrome</h3>
                            <p className="text-md text-gray-600 max-w-md">
                                Chrome's V8 engine provides the superior memory management required to prevent tab-freezes when calculating massive datasets.
                            </p>
                        </div>
                    </div>
                    <a 
                        href="https://www.google.com/chrome/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="relative z-10 whitespace-nowrap px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl transition-all shadow-md hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                        Download Chrome
                    </a>
                </div>

            </div>
        </div>
    );
};

export default RecommendedRequirements;